import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge } from '@mono/ui-components';
import { memo, useCallback, useMemo } from 'react';
import { CostSummary, GroupSection } from './PricingTableParts';
import { TierCompositionEditor } from './TierCompositionEditor';
export const TMClinEditor = /*#__PURE__*/ memo(function TMClinEditor({ clinPricing, rateCard, onChange }) {
    const { laborStructure, periods, metadata } = clinPricing;
    // T&M: rate is editable (contractor-proposed), no fee
    const columns = useMemo(()=>{
        const result = [
            ...clinPricing.columns
        ];
        // Make rate editable for T&M
        const rateCol = result.find((c)=>c.key === 'rate');
        if (rateCol) rateCol.editable = true;
        if (!result.some((c)=>c.key === 'ftes') && clinPricing.volumeMethod.type === 'contractor_proposed' && clinPricing.volumeMethod.hasFteColumn) {
            const labelIdx = result.findIndex((c)=>c.key === 'label');
            result.splice(labelIdx + 1, 0, {
                key: 'ftes',
                label: 'FTEs',
                parameterName: 'ftes',
                editable: true,
                perPeriod: false
            });
        }
        return result;
    }, [
        clinPricing.columns,
        clinPricing.volumeMethod
    ]);
    const handleParamChange = useCallback((lineId, paramName, value, period)=>{
        const updateLine = (line)=>{
            if (line.id !== lineId) return line;
            const param = line.parameters[paramName];
            if (!param) return line;
            let updatedParam;
            if (period && param.perPeriod) {
                updatedParam = {
                    ...param,
                    periodValues: {
                        ...param.periodValues,
                        [period]: value ?? 0
                    }
                };
            } else {
                updatedParam = {
                    ...param,
                    value
                };
            }
            return {
                ...line,
                parameters: {
                    ...line.parameters,
                    [paramName]: updatedParam
                }
            };
        };
        const updateGroup = (group)=>({
                ...group,
                lines: group.lines.map(updateLine)
            });
        let updated;
        switch(laborStructure.type){
            case 'direct':
            case 'government_defined':
                updated = {
                    ...clinPricing,
                    laborStructure: {
                        ...laborStructure,
                        groups: laborStructure.groups.map(updateGroup)
                    }
                };
                break;
            case 'tiered':
                updated = {
                    ...clinPricing,
                    laborStructure: {
                        ...laborStructure,
                        tiers: laborStructure.tiers.map((t)=>({
                                ...t,
                                group: updateGroup(t.group)
                            }))
                    }
                };
                break;
            default:
                updated = clinPricing;
        }
        onChange(updated);
    }, [
        clinPricing,
        laborStructure,
        onChange
    ]);
    const handleFteChange = useCallback((lineId, fteCount)=>{
        const updateLine = (line)=>line.id === lineId ? {
                ...line,
                fteCount
            } : line;
        const updateGroup = (group)=>({
                ...group,
                lines: group.lines.map(updateLine)
            });
        let updated;
        switch(laborStructure.type){
            case 'direct':
            case 'government_defined':
                updated = {
                    ...clinPricing,
                    laborStructure: {
                        ...laborStructure,
                        groups: laborStructure.groups.map(updateGroup)
                    }
                };
                break;
            case 'tiered':
                updated = {
                    ...clinPricing,
                    laborStructure: {
                        ...laborStructure,
                        tiers: laborStructure.tiers.map((t)=>({
                                ...t,
                                group: updateGroup(t.group)
                            }))
                    }
                };
                break;
            default:
                updated = clinPricing;
        }
        onChange(updated);
    }, [
        clinPricing,
        laborStructure,
        onChange
    ]);
    const handleTierChange = useCallback((tierIndex, updatedTier)=>{
        if (laborStructure.type !== 'tiered') return;
        const newTiers = laborStructure.tiers.map((t, i)=>i === tierIndex ? updatedTier : t);
        onChange({
            ...clinPricing,
            laborStructure: {
                ...laborStructure,
                tiers: newTiers
            }
        });
    }, [
        clinPricing,
        laborStructure,
        onChange
    ]);
    // NTE ceiling info
    const nteCeiling = metadata.nteCeiling;
    // Tiered
    if (laborStructure.type === 'tiered') {
        return /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center gap-2 flex-wrap",
                    children: [
                        /*#__PURE__*/ _jsx(Badge, {
                            variant: "outline",
                            className: "text-xs bg-purple-50 text-purple-700 border-purple-200",
                            children: "T&M"
                        }),
                        metadata.periodName && /*#__PURE__*/ _jsx(Badge, {
                            variant: "outline",
                            className: "text-xs bg-blue-50 text-blue-700 border-blue-200",
                            children: metadata.periodName
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "text-[10px] text-gray-400 italic",
                            children: "Rate x Hours, no fee"
                        }),
                        nteCeiling != null && /*#__PURE__*/ _jsxs(Badge, {
                            variant: "outline",
                            className: "text-xs bg-red-50 text-red-700 border-red-200",
                            children: [
                                "NTE $",
                                nteCeiling.toLocaleString()
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "space-y-6",
                    children: laborStructure.tiers.map((tier, idx)=>/*#__PURE__*/ _jsx("div", {
                            className: "border border-gray-200 rounded-lg p-4 bg-white",
                            children: /*#__PURE__*/ _jsx(TierCompositionEditor, {
                                tier: tier,
                                rateCard: rateCard,
                                columns: columns,
                                periods: periods,
                                onTierChange: (updated)=>handleTierChange(idx, updated)
                            })
                        }, tier.group.id))
                }),
                /*#__PURE__*/ _jsx(CostSummary, {
                    clinPricing: clinPricing
                })
            ]
        });
    }
    // Direct
    let groups = [];
    if (laborStructure.type === 'direct' || laborStructure.type === 'government_defined') {
        groups = laborStructure.groups.map((g)=>({
                label: g.label,
                group: g,
                collapsible: laborStructure.groups.length > 1
            }));
    }
    const headerCols = [];
    for (const col of columns){
        if (col.perPeriod && periods.length > 0) {
            for (const period of periods)headerCols.push({
                key: `${col.key}-${period}`,
                label: `${col.label} (${period})`
            });
        } else {
            headerCols.push({
                key: col.key,
                label: col.label
            });
        }
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 flex-wrap",
                children: [
                    /*#__PURE__*/ _jsx(Badge, {
                        variant: "outline",
                        className: "text-xs bg-purple-50 text-purple-700 border-purple-200",
                        children: "T&M"
                    }),
                    metadata.periodName && /*#__PURE__*/ _jsx(Badge, {
                        variant: "outline",
                        className: "text-xs bg-blue-50 text-blue-700 border-blue-200",
                        children: metadata.periodName
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-[10px] text-gray-400 italic",
                        children: "Rate x Hours, no fee"
                    }),
                    nteCeiling != null && /*#__PURE__*/ _jsxs(Badge, {
                        variant: "outline",
                        className: "text-xs bg-red-50 text-red-700 border-red-200",
                        children: [
                            "NTE $",
                            nteCeiling.toLocaleString()
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "overflow-x-auto",
                children: /*#__PURE__*/ _jsxs("table", {
                    className: "w-full border-collapse",
                    children: [
                        /*#__PURE__*/ _jsx("thead", {
                            children: /*#__PURE__*/ _jsx("tr", {
                                className: "border-b-2 border-gray-200",
                                children: headerCols.map((h)=>/*#__PURE__*/ _jsx("th", {
                                        className: `px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider ${h.key === 'label' || h.key.startsWith('label') ? 'text-left' : 'text-right'}`,
                                        children: h.label
                                    }, h.key))
                            })
                        }),
                        /*#__PURE__*/ _jsx("tbody", {
                            children: groups.map((g)=>/*#__PURE__*/ _jsx(GroupSection, {
                                    group: g.group,
                                    columns: columns,
                                    periods: periods,
                                    collapsible: g.collapsible,
                                    onParamChange: handleParamChange,
                                    onFteChange: handleFteChange
                                }, g.group.id))
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(CostSummary, {
                clinPricing: clinPricing
            })
        ]
    });
});
